<template>
  <Teleport to="body" :disabled="!isFullscreen">
    <div
      ref="rootRef"
      class="media-player"
      :class="{
        'media-player--fullscreen': isFullscreen,
        'media-player--fill-container': props.fillContainer,
        [`media-player--size-${props.size}`]: props.size,
      }"
      @mouseenter="handleMouseEnter"
      @mousemove="handleMouseMove"
      @mouseleave="handleMouseLeave"
    >
      <BaseAspectRatio
        :width="aspectRatioWidth"
        :height="aspectRatioHeight"
        :with-full-height="useFullHeightAspect"
      >
        <video
          ref="videoRef"
          :poster="props.poster"
          :autoplay="props.autoplay"
          :muted="props.muted"
          :controls="false"
          playsinline
          webkit-playsinline
          disablePictureInPicture
          controlsList="nodownload noremoteplayback nofullscreen"
          @click="handleTogglePlay"
          @timeupdate="handleTimeUpdate"
          @loadedmetadata="handleLoadedMetadata"
          @play="handlePlay"
          @pause="handlePause"
          @ended="handleEnded"
          @waiting="handleWaiting"
          @canplay="handleCanPlay"
        />
      </BaseAspectRatio>
      <div
        class="media-player__wrapper"
        :class="{ 'media-player__wrapper--hidden': !showControls }"
      >
        <div class="media-player__top-controls">
          <BaseButtonIcon
            v-if="props.withCloseButton && !isFullscreen"
            icon-name="vuesax:trash-linear"
            icon-color="var(--color-foreground-inverted)"
            variant="transparent-inverted"
            @click="handleClose"
          />

          <BaseButtonIcon
            :icon-name="isFullscreen ? 'vuesax:close-linear' : 'vuesax:open-full-linear'"
            icon-color="var(--color-foreground-inverted)"
            variant="transparent-inverted"
            @click="handleToggleFullscreen()"
          />
        </div>

        <div v-if="props.size === 'mini'" class="media-player__play-button">
          <BaseLoader v-if="isBuffering" color="white" size="24px" />
          <BaseIcon
            v-else
            :name="isPlaying ? 'vuesax:pause-bold' : 'vuesax:play-bold'"
            fill-type="solid"
            color="var(--color-foreground-inverted)"
            size="24px"
            clickable
            @click="handleTogglePlay"
          />
        </div>

        <div class="media-player__controls">
          <MediaPlayerControls
            :current-time="currentTime"
            :duration="duration"
            :play-progress="playProgress"
            :is-playing="isPlaying"
            :is-local="!srcIsHlsVideo"
            @seek-to="handleSeekTo"
            @toggle-play="handleTogglePlay"
            @change-quality="handleChangeQuality"
            @change-speed="handleChangeSpeed"
          />
          <!-- <BaseIcon
            :name="isPlaying ? 'vuesax:pause-circle-bold' : 'vuesax:play-circle-bold'"
            fill-type="solid"
            color="var(--color-foreground-inverted)"
            size="38px"
            clickable
            @click="handleTogglePlay"
          />
          <div class="media-player__timeline">
            <BaseTypography
              type="subtitle"
              color="var(--color-foreground-inverted)"
            >
              {{ formatTime(currentTime) }}
            </BaseTypography>

            <div class="media-player__progress-container" @click="handleSeekTo">
              <BaseProgressLinear
                :progress="playProgress"
                height="4px"
                variant="light"
                :animated="false"
              />
            </div>

            <BaseTypography
              type="subtitle"
              color="var(--color-foreground-inverted)"
            >
              {{ formatTime(duration) }}
            </BaseTypography>
          </div>
          <div class="media-player__controls-right-side">
            <BaseIcon
              name="vuesax:backward-10-seconds-linear"
              size="30px"
              color="white"
              clickable
              @click="handleRewind"
            />

            <BaseIcon
              name="vuesax:forward-10-seconds-linear"
              size="30px"
              color="white"
              clickable
              @click="handleForward"
            />

            <BaseIcon
              :name="isFullscreen ? 'vuesax:toggle-image-linear' : 'vuesax:open-full-linear'"
              size="30px"
              color="white"
              clickable
              @click="handleToggleFullscreen()"
            />
          </div> -->
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
// lib
import Hls from 'hls.js'

// app
import { isHlsVideoUrl } from '~/shared/lib'
import { BaseAspectRatio, BaseButtonIcon, BaseIcon, BaseLoader } from '~/shared/ui'
import MediaPlayerControls from './MediaPlayerControls.vue'

interface Props {
  src: string
  poster?: string
  autoplay?: boolean
  muted?: boolean
  controls?: boolean
  width?: string | number
  height?: string | number
  size?: 'mini' | 'md' | 'lg'
  withCloseButton?: boolean
  aspectWidth?: number
  aspectHeight?: number
  fillContainer?: boolean
  matchContainerAspect?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  autoplay: false,
  muted: false,
  width: '100%',
  height: 'auto',
  size: 'md',
  aspectWidth: 16,
  aspectHeight: 9,
  fillContainer: false,
  matchContainerAspect: false,
})

const emit = defineEmits<{
  (e: 'play'): void
  (e: 'pause'): void
  (e: 'ended'): void
  (e: 'error', error: Error): void
  (e: 'close'): void
  (e: 'timeupdate', currentTime: number, duration: number): void
}>()

const rootRef = ref<HTMLElement | null>(null)
const videoRef = ref<HTMLVideoElement | null>(null)
const hls = ref<Hls | null>(null)
let visibilityObserver: IntersectionObserver | null = null
const isBuffering = ref(false)
const isPlaying = ref(false)
const showControls = ref(true)
const controlsTimeout = ref<NodeJS.Timeout | null>(null)
const currentTime = ref(0)
const duration = ref(0)
const isFullscreen = ref(false)

const srcIsHlsVideo = computed(() => isHlsVideoUrl(props.src))
const playProgress = computed(() => {
  if (duration.value === 0) {
    return 0
  }
  return (currentTime.value / duration.value) * 100
})

const useContainerAspect = computed(() => (
  props.matchContainerAspect || props.size === 'mini'
))
const aspectRatioWidth = computed(() => (
  useContainerAspect.value ? props.aspectWidth : 16
))
const aspectRatioHeight = computed(() => (
  useContainerAspect.value ? props.aspectHeight : 9
))
const useFullHeightAspect = computed(() => (
  isFullscreen.value || props.size === 'mini'
))

watch(() => props.src, () => {
  if (hls.value) {
    hls.value.destroy()
    hls.value = null
  }

  if (!props.src) {
    return
  }

  nextTick(() => {
    initPlayer()
  })
}, { immediate: true })

function teardownVisibilityObserver() {
  if (visibilityObserver) {
    visibilityObserver.disconnect()
    visibilityObserver = null
  }
}

function handlePlayerVisibility(entries: IntersectionObserverEntry[]) {
  const entry = entries[0]
  if (!entry || !videoRef.value || isFullscreen.value) {
    return
  }

  if (!entry.isIntersecting && !videoRef.value.paused) {
    videoRef.value.pause()
  }
}

function setupVisibilityObserver() {
  teardownVisibilityObserver()

  if (!rootRef.value || typeof IntersectionObserver === 'undefined') {
    return
  }

  visibilityObserver = new IntersectionObserver(handlePlayerVisibility, {
    root: null,
    threshold: [0, 0.01],
  })
  visibilityObserver.observe(rootRef.value)
}

onMounted(() => {
  document.addEventListener('keydown', handleKeyDown)
  nextTick(() => {
    setupVisibilityObserver()
  })
})

onUnmounted(() => {
  teardownVisibilityObserver()

  if (hls.value) {
    hls.value.destroy()
  }

  if (controlsTimeout.value) {
    clearTimeout(controlsTimeout.value)
  }

  document.removeEventListener('keydown', handleKeyDown)

  if (isFullscreen.value) {
    document.body.style.overflow = ''
  }
})

function initPlayer() {
  if (!videoRef.value) {
    return
  }

  if (!srcIsHlsVideo.value) {
    videoRef.value.src = props.src
    return
  }

  initHls()
}

function initHls() {
  if (!videoRef.value || !Hls.isSupported()) {
    return
  }

  hls.value = new Hls({
    debug: false,
    enableWorker: true,
    lowLatencyMode: true,
  })
  hls.value.loadSource(props.src)
  hls.value.attachMedia(videoRef.value)

  hls.value.on(Hls.Events.BUFFER_APPENDING, () => {
    isBuffering.value = true
  })

  hls.value.on(Hls.Events.BUFFER_APPENDED, () => {
    isBuffering.value = false
  })

  hls.value.on(Hls.Events.LEVEL_SWITCHING, () => {
    isBuffering.value = true
  })

  hls.value.on(Hls.Events.LEVEL_SWITCHED, () => {
    isBuffering.value = false
  })
}

// function handleChangeQuality(quality: IMediaDropdownOption<Quality>, cb?: () => void) {
//   cb?.()

//   if (!hls.value) {
//     return
//   }

//   const levelIndex = hls.value.levels.findIndex(level => level.height === quality.value)
//   hls.value.currentLevel = levelIndex
//   currentQuality.value = quality.name
// }

// function handleChangeSpeed(speed: IMediaDropdownOption<Speed>, cb?: () => void) {
//   cb?.()

//   if (!videoRef.value) {
//     return
//   }

//   videoRef.value.playbackRate = speed.value
//   currentSpeed.value = speed.name
// }

function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape' && isFullscreen.value) {
    handleToggleFullscreen()
  }
}

function handleToggleFullscreen(value?: boolean) {
  isFullscreen.value = value ?? !isFullscreen.value

  if (isFullscreen.value) {
    document.body.style.overflow = 'hidden'
  }
  else {
    document.body.style.overflow = ''
  }
}

function handleChangeQuality(quality: number) {
  if (!hls.value) {
    return
  }

  const levelIndex = hls.value.levels.findIndex(level => level.height === quality)
  hls.value.currentLevel = levelIndex
}

function handleChangeSpeed(speed: number) {
  if (!videoRef.value) {
    return
  }
  videoRef.value.playbackRate = speed
}

function handleClose() {
  emit('close')
  handleToggleFullscreen(false)
}

function showControlsTemporarily() {
  showControls.value = true

  if (controlsTimeout.value) {
    clearTimeout(controlsTimeout.value)
  }

  controlsTimeout.value = setTimeout(() => {
    if (isPlaying.value) {
      showControls.value = false
    }
  }, 2000)
}

function handleMouseEnter() {
  showControlsTemporarily()
}

function handleMouseMove() {
  showControlsTemporarily()
}

function handleMouseLeave() {
  if (isPlaying.value) {
    showControls.value = false
    if (controlsTimeout.value) {
      clearTimeout(controlsTimeout.value)
    }
  }
}

function handleTimeUpdate() {
  if (!videoRef.value) {
    return
  }
  currentTime.value = videoRef.value.currentTime
  emit('timeupdate', currentTime.value, duration.value)
}

function handleLoadedMetadata() {
  if (!videoRef.value) {
    return
  }
  duration.value = videoRef.value.duration
}

function handlePlay() {
  isPlaying.value = true
  emit('play')
  showControlsTemporarily()
}

function handlePause() {
  isPlaying.value = false
  emit('pause')
  showControls.value = true
}

function handleEnded() {
  isPlaying.value = false
  emit('ended')
  showControls.value = true
}

function handleWaiting() {
  isBuffering.value = true
}

function handleCanPlay() {
  isBuffering.value = false
}

async function handleTogglePlay() {
  if (!videoRef.value) {
    return
  }

  try {
    if (isPlaying.value) {
      videoRef.value.pause()
    }
    else {
      await videoRef.value.play()
    }
  }
  catch (error) {
    console.error('Error toggling play:', error)
    emit('error', error as Error)
  }
}

function handleSeekTo(event: MouseEvent) {
  if (!videoRef.value || !duration.value) {
    return
  }

  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
  const clickX = event.clientX - rect.left
  const percentage = clickX / rect.width
  const newTime = percentage * duration.value

  videoRef.value.currentTime = newTime
}

// function handleRewind() {
//   if (!videoRef.value) {
//     return
//   }
//   videoRef.value.currentTime = Math.max(0, videoRef.value.currentTime - 10)
// }

// function handleForward() {
//   if (!videoRef.value || !duration.value) {
//     return
//   }
//   videoRef.value.currentTime = Math.min(duration.value, videoRef.value.currentTime + 10)
// }
</script>

<style scoped lang="scss">
.media-player {
  --color-surface-inverted-900: #000;
  --color-foreground-inverted: #fff;
  --color-surface-transparent-inverted-900: #000000b2;

  position: relative;
  width: v-bind(width);
  height: v-bind(height);
  background-color: var(--color-surface-inverted-900);
  border-radius: var(--radius-md);
  overflow: hidden;

  &:deep(video) {
    height: 100%;
    width: 100%;
    object-fit: contain;
    cursor: pointer;
  }

  &__wrapper {
    position: absolute;
    inset: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;

    > * {
      pointer-events: auto;
    }

    &--hidden {
      opacity: 0;
    }
  }

  &__top-controls {
    position: absolute;
    top: rem(16px);
    right: rem(16px);
    display: flex;
    align-items: center;
    gap: rem(10px);
    z-index: 10;

    @include bp-media('xs') {
      top: 8px;
      right: 8px;
      gap: 8px;
    }
  }

  &__play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) !important;
    z-index: 5;
  }

  &__controls {
    position: absolute;
    max-width: 550px;
    width: 100%;
    left: 50%;
    bottom: rem(16px);
    padding-left: rem(12px);
    padding-right: rem(12px);
    transform: translateX(-50%);

    @include bp-media('xs', 'sm', 'md') {
      max-width: 320px;
    }

    @include bp-media('xs') {
      bottom: rem(12px);
    }
  }

  &__timeline {
    flex: 1;
    display: flex;
    align-items: center;
    gap: rem(10px);

    @include bp-media('xs') {
      gap: 8px;
    }
  }

  &--size {
    &-mini {
      width: 100%;
      height: 100%;
      border-radius: var(--radius-xxs);

      .media-player__top-controls {
        display: none;
      }

      .media-player__controls {
        display: none;
      }
    }

    &-md {
      border-radius: var(--radius-md);
    }
  }

  &--fill-container {
    width: 100%;
    height: 100%;
  }

  &--fullscreen {
    position: fixed;
    inset: 0;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999;
    border-radius: 0;

    .media-player__top-controls {
      top: 24px;
      right: 24px;
    }
  }
}
</style>
